/**
 * Density Telemetry Panel (P0.1 · Sprint P28 · P0.5)
 * ─────────────────────────────────────────────────────
 * Adds: automatic detection + user-editable project default density.
 * Uses backend endpoints:
 *   GET   /api/projects/{pid}/density/default
 *   PATCH /api/projects/{pid}/density/default
 *   POST  /api/projects/{pid}/density/auto-assign
 *   GET   /api/projects/{pid}/density/telemetry
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Warning, CheckCircle, MagicWand, Pencil, FloppyDisk, Info,
} from "@phosphor-icons/react";
import { WizardCard, StatusRow, KV } from "@/pages/wizard/_shared";
import { api, getDensityTelemetry } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";


const PRIORITY_COLOUR = {
  1: "bg-emerald-100 text-emerald-800 border-emerald-300",
  2: "bg-emerald-100 text-emerald-800 border-emerald-300",
  3: "bg-sky-100 text-sky-800 border-sky-300",
  4: "bg-sky-100 text-sky-800 border-sky-300",
  5: "bg-amber-100 text-amber-800 border-amber-300",
  6: "bg-amber-100 text-amber-800 border-amber-300",
  7: "bg-red-100 text-red-800 border-red-300",
};


function errMsg(e, fb = "Request failed") {
  const d = e?.response?.data;
  if (typeof d === "string") return d;
  if (typeof d?.detail === "string") return d.detail;
  if (Array.isArray(d?.detail)) return d.detail.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return e?.message || fb;
}


// ─────────────────────────────────────────────────────────────────────
// Density resolution toolbar — auto-detect + edit default
// ─────────────────────────────────────────────────────────────────────
function DensityResolverToolbar({ projectId, hasMissing, missingCount }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState("");

  const defQ = useQuery({
    queryKey: ["density-default", projectId],
    queryFn: () => api.get(`/projects/${projectId}/density/default`).then((r) => r.data),
    enabled: !!projectId,
  });

  useEffect(() => {
    if (defQ.data?.default_density != null) {
      setEditVal(String(defQ.data.default_density));
    } else if (defQ.data?.auto_recommended != null) {
      setEditVal(String(defQ.data.auto_recommended));
    }
  }, [defQ.data]);

  const autoMut = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/density/auto-assign`,
      { strategy: "project_default" }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`Auto-assigned · ${d.reassigned_blocks} blocks stamped at ${d.value} t/m³ · +${(d.tonnes_added / 1e6).toFixed(2)} Mt`);
      qc.invalidateQueries({ queryKey: ["density-telemetry", projectId] });
      qc.invalidateQueries({ queryKey: ["density-default", projectId] });
      qc.invalidateQueries({ queryKey: ["health", projectId] });
      qc.invalidateQueries({ queryKey: ["cpr-approval-gate", projectId] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const setMut = useMutation({
    mutationFn: (val) => api.patch(`/projects/${projectId}/density/default`,
      { default_density: Number(val), reassign_blocks: true }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`Default density set to ${d.default_density} t/m³ · ${d.reassigned_blocks} blocks updated`);
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["density-telemetry", projectId] });
      qc.invalidateQueries({ queryKey: ["density-default", projectId] });
      qc.invalidateQueries({ queryKey: ["health", projectId] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const cur    = defQ.data?.default_density;
  const rec    = defQ.data?.auto_recommended;
  const unit   = defQ.data?.density_unit || "t/m³";
  const cmdty  = defQ.data?.commodity;

  return (
    <div className="border border-sky-200 bg-sky-50/60 rounded-md p-3 mb-3"
         data-testid="density-resolver-toolbar">
      <div className="flex items-center gap-2 mb-2">
        <MagicWand size={14} className="text-sky-700" weight="duotone" />
        <div className="text-[11px] font-bold uppercase tracking-widest text-sky-900">
          Density resolution
        </div>
        <div className="ml-auto text-[10px] font-mono text-slate-500">
          Level 6 · project-default source
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-700">
        <div className="flex items-center gap-1">
          <span className="text-slate-500">Current default:</span>
          <span className="font-mono font-bold text-slate-900"
                data-testid="density-current-default">
            {cur != null ? `${cur} ${unit}` : "— (not set)"}
          </span>
        </div>
        {cmdty && (
          <div className="text-[10px] text-slate-500">
            · Commodity {cmdty} · SG heuristic {rec} {unit}
          </div>
        )}
        <div className="flex-1" />
        {!editing ? (
          <>
            <Button size="sm"
                    onClick={() => autoMut.mutate()}
                    disabled={autoMut.isPending || !hasMissing}
                    title={hasMissing
                      ? `Stamp density_source=project_default on ${missingCount} block${missingCount === 1 ? "" : "s"} and recompute tonnage`
                      : "No blocks currently missing a density source"}
                    className="h-7 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="density-auto-assign">
              <MagicWand size={11} className="mr-1" />
              {autoMut.isPending ? "Auto-detecting…" : `Auto-detect & assign${hasMissing ? ` (${missingCount})` : ""}`}
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => setEditing(true)}
                    className="h-7 text-[10.5px]"
                    data-testid="density-edit">
              <Pencil size={11} className="mr-1" /> Edit default
            </Button>
          </>
        ) : (
          <>
            <Input type="number" step="0.05" min="0.1" max="25"
                   value={editVal}
                   onChange={(e) => setEditVal(e.target.value)}
                   className="h-7 w-24 text-[11px] font-mono"
                   data-testid="density-edit-input" />
            <span className="text-[10px] font-mono text-slate-500">{unit}</span>
            <Button size="sm"
                    onClick={() => setMut.mutate(editVal)}
                    disabled={setMut.isPending || !editVal || Number(editVal) <= 0}
                    className="h-7 text-[10.5px] bg-sky-600 hover:bg-sky-700 text-white"
                    data-testid="density-save">
              <FloppyDisk size={11} className="mr-1" /> Save &amp; restamp
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => { setEditing(false); setEditVal(String(cur ?? rec ?? "")); }}
                    className="h-7 text-[10.5px]"
                    data-testid="density-cancel">
              Cancel
            </Button>
          </>
        )}
      </div>

      <div className="mt-2 flex items-start gap-1.5 text-[10px] text-slate-600">
        <Info size={11} className="mt-0.5 text-slate-400" />
        <div>
          <b>Auto-detect</b> stamps every unresolved block with the project default
          density and recomputes tonnage from the block volume ({unit}).
          <b> Edit</b> overrides the project default and re-stamps every block whose
          current source is <i>project_default</i> or <i>MISSING</i>. Higher-priority
          sources (block-estimated / measured sample / domain / lithology) are
          preserved.
        </div>
      </div>
    </div>
  );
}


export default function DensityTelemetryPanel({ projectId }) {
  const q = useQuery({
    queryKey: ["density-telemetry", projectId],
    queryFn:  () => getDensityTelemetry(projectId),
    enabled:  !!projectId,
    refetchInterval: 30_000,
  });

  const d = q.data;
  const hasMissing = d && d.n_missing > 0;

  return (
    <WizardCard
        title="Density Priority Telemetry"
        subtitle="Blocks grouped by density source · levels 1-6 accepted · level 7 (missing) excluded"
        testId="w-density-telemetry">
      {q.isLoading && <div className="text-[11px] text-slate-500">Loading…</div>}
      {q.isError && <StatusRow tone="danger">Backend unavailable — build a block model first.</StatusRow>}
      {d && d.n_blocks === 0 && (
        <StatusRow tone="info">No block model yet — telemetry becomes available once a block model is built.</StatusRow>
      )}

      {d && d.n_blocks > 0 && (
        <>
          <DensityResolverToolbar projectId={projectId}
                                    hasMissing={hasMissing}
                                    missingCount={d.n_missing || 0} />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <KV k="Total blocks"    v={d.n_blocks}                          testId="w-density-total" />
            <KV k="Blocks missing"  v={d.n_missing}                         testId="w-density-missing" />
            <KV k="Tonnes excluded" v={`${(d.tonnes_excluded / 1e3).toLocaleString(undefined,{maximumFractionDigits:1})} kt`}
                testId="w-density-tonnes-excluded" />
            <KV k="Priority levels" v="7" testId="w-density-levels" />
          </div>

          {d.warnings?.map((w, i) => (
            <div key={i} className="border border-red-300 bg-red-50 rounded p-3 mb-3"
                  data-testid={`w-density-warning-${i}`}>
              <div className="flex items-start gap-2 text-[12px]">
                <Warning size={16} className="text-red-600 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-red-800">{w.error_code} · {w.severity}</div>
                  <div className="text-red-700 mt-1"><b>Reason:</b> {w.reason}</div>
                  <div className="text-red-700 mt-1"><b>Actual:</b> {w.actual_value}</div>
                  <div className="text-red-700 mt-1"><b>Expected:</b> {w.expected_requirement}</div>
                  <div className="text-red-700 mt-1"><b>Corrective action:</b> {w.corrective_action}</div>
                </div>
              </div>
            </div>
          ))}

          {(!d.warnings || d.warnings.length === 0) && (
            <div className="border border-emerald-300 bg-emerald-50 rounded p-2 mb-3 text-[12px] flex items-center gap-2"
                  data-testid="w-density-ok">
              <CheckCircle size={14} className="text-emerald-600" />
              Every block has an approved density source.
            </div>
          )}

          <div className="rounded border border-slate-200 overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-2 py-1 w-16">Priority</th>
                  <th className="text-left px-2 py-1">Source</th>
                  <th className="text-right px-2 py-1">Blocks</th>
                  <th className="text-right px-2 py-1">Ore</th>
                  <th className="text-right px-2 py-1">Waste</th>
                  <th className="text-right px-2 py-1">Tonnes</th>
                  <th className="text-right px-2 py-1">%</th>
                </tr>
              </thead>
              <tbody>
                {d.distribution.map((r) => (
                  <tr key={r.source} className="border-t border-slate-200"
                        data-testid={`w-density-row-${r.source}`}>
                    <td className="px-2 py-1">
                      <span className={`px-1.5 py-0.5 rounded border ${PRIORITY_COLOUR[r.priority]}`}>
                        {r.priority}
                      </span>
                    </td>
                    <td className="px-2 py-1 font-bold text-slate-800">{r.label}</td>
                    <td className="text-right px-2 py-1">{r.n_blocks.toLocaleString()}</td>
                    <td className="text-right px-2 py-1 text-emerald-600">{r.ore_blocks.toLocaleString()}</td>
                    <td className="text-right px-2 py-1 text-slate-500">{r.waste_blocks.toLocaleString()}</td>
                    <td className="text-right px-2 py-1">{(r.tonnes / 1e3).toLocaleString(undefined,{maximumFractionDigits:1})}k</td>
                    <td className="text-right px-2 py-1">{r.percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </WizardCard>
  );
}
